import { memo, useMemo, useRef } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import Input from '@atlas/uicorn/lib/Input/Input';
import Select from '@atlas/uicorn/lib/Input/Select';
import countryNames from '@atlas/tool/box/countryNames';

const AddressInput = memo(function AddressInput(props: TAddressInputProps): React.ReactElement {
    const valueRef = useRef<CustomFieldAddressValue>({ ...props.value });

    const countryOptions = useMemo(
        () => Object.entries(countryNames).map(([code, name]) => ({ key: code, label: name })),
        [],
    );

    return (
        <>
            <SInputFormItemLabel $disabled={props.disabled}>Street 1</SInputFormItemLabel>
            <Input
                disabled={props.disabled}
                placeholder="Enter street 1..."
                onChange={(e) => {
                    valueRef.current.street1 = e.target.value;
                    props.onChange?.({ ...valueRef.current, street1: e.target.value });
                }}
                value={valueRef.current.street1}
            />
            <SInputsSpacer />
            <SInputFormItemLabel $disabled={props.disabled}>Street 2</SInputFormItemLabel>
            <Input
                disabled={props.disabled}
                placeholder="Enter street 2..."
                onChange={(e) => {
                    valueRef.current.street2 = e.target.value;
                    props.onChange?.({ ...valueRef.current, street2: e.target.value });
                }}
                value={valueRef.current.street2}
            />
            <SInputsSpacer />
            <SInputFormItemLabel $disabled={props.disabled}>City</SInputFormItemLabel>
            <Input
                disabled={props.disabled}
                placeholder="Enter city..."
                onChange={(e) => {
                    valueRef.current.city = e.target.value;
                    props.onChange?.({ ...valueRef.current, city: e.target.value });
                }}
                value={valueRef.current.city}
            />
            <SInputsSpacer />
            <SInputFormItemLabel $disabled={props.disabled}>State/Province/Region</SInputFormItemLabel>
            <Input
                disabled={props.disabled}
                placeholder="Enter state..."
                onChange={(e) => {
                    valueRef.current.state = e.target.value;
                    props.onChange?.({ ...valueRef.current, state: e.target.value });
                }}
                value={valueRef.current.state}
            />
            <SInputsSpacer />
            <SInputFormItemLabel $disabled={props.disabled}>ZIP code</SInputFormItemLabel>
            <Input
                disabled={props.disabled}
                placeholder="Enter ZIP code..."
                onChange={(e) => {
                    valueRef.current.zip = e.target.value;
                    props.onChange?.({ ...valueRef.current, zip: e.target.value });
                }}
                value={valueRef.current.zip}
            />
            <SInputsSpacer />
            <SInputFormItemLabel $disabled={props.disabled}>Country</SInputFormItemLabel>
            <Select
                placeholder="Select country..."
                options={countryOptions}
                onChange={(value) => {
                    // TODO: help center build fails due to allowed number type for `key` in TSelectOption
                    const selectedCountryCode = value as string | undefined;

                    valueRef.current.country = selectedCountryCode;
                    props.onChange?.({ ...valueRef.current, country: selectedCountryCode });
                }}
                value={valueRef.current.country}
            />
        </>
    );
});

export default AddressInput;

// #region Styles

const SInputsSpacer = styled.div`
    padding-top: var(--form-gap);
`;

const SInputFormItemLabel = styled.div<{ $disabled?: boolean }>`
    font-size: 1.3rem;
    padding-bottom: 0.8rem;
    font-weight: 550;
    color: ${(p) => (p.$disabled ? p.theme.color.grey8 : p.theme.color.grey9)};
`;

// #endregion

// #region Types

export type TAddressInputProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'onChange'> & {
    disabled?: boolean;
    value?: CustomFieldAddressValue | null;
    onChange?: (value?: CustomFieldAddressValue) => void;
};

export type CustomFieldAddressValue = {
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('AddressInput', async () => {
        // Disabled because of <Select /> context issue
        // const { render } = await import(/* @vite-ignore */ '@test');

        it.todo('matches snapshot');
    });
}

// #endregion
